import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  DragHandle as DragHandleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { useSnackbar } from "notistack";
import { CKEditor } from "ckeditor4-react";
import {
  getCalculationsModel,
  saveCalculationsModel,
} from "../../services/api/calculations";
import { calculationsCache } from "../../utils/cache";

// Precision helper functions
const toMoney2 = (n) => Math.round(Number(n) * 100) / 100;
const toCzkInt = (n) => Math.round(Number(n));

// Component calculation functions (available to child components)
const calcComponentPurchaseTotal = (unitPrice, quantity) => {
  const safeUnitPrice = Math.max(0, Number(unitPrice) || 0);
  const safeQuantity = Math.max(0, Number(quantity) || 0);
  return safeUnitPrice * safeQuantity;
};

const calcComponentTotalWithMargin = (unitPrice, marginPercent, quantity) => {
  const purchaseTotal = calcComponentPurchaseTotal(unitPrice, quantity);
  const clampedMargin = Math.min(500, Math.max(0, Number(marginPercent) || 0)); // Allow up to 500% margin
  return purchaseTotal * (1 + clampedMargin / 100);
};

// Normalize components from different schema versions
const normalizeComponents = (product) => {
  if (!product) return [];

  const generateStableId = (inputId, index) => {
    return inputId || `comp_${index}_${Date.now()}`;
  };

  // Schema A: New schema - product.components is Array
  if (product.components && Array.isArray(product.components)) {
    return product.components.map((component, index) => ({
      id: component.id || generateStableId(component.inputId, index),
      name: component.name || "",
      unitPrice:
        typeof component.unitPrice === "number" ? component.unitPrice : 0,
      marginPercent:
        typeof component.marginPercent === "number"
          ? component.marginPercent
          : 0,
      quantity: typeof component.quantity === "number" ? component.quantity : 1,
      isRequired:
        typeof component.isRequired === "boolean" ? component.isRequired : true,
    }));
  }

  // Schema B: Old schema - product.components is { required:[], optional:[] }
  if (
    product.components &&
    typeof product.components === "object" &&
    (product.components.required || product.components.optional)
  ) {
    const normalized = [];

    // Add required components
    if (Array.isArray(product.components.required)) {
      product.components.required.forEach((component, index) => {
        const { isSelected, ...cleanComponent } = component; // Remove isSelected
        normalized.push({
          id: component.id || generateStableId(component.inputId, index),
          name: component.name || "",
          unitPrice:
            typeof component.unitPrice === "number" ? component.unitPrice : 0,
          marginPercent:
            typeof component.marginPercent === "number"
              ? component.marginPercent
              : 0,
          quantity:
            typeof component.quantity === "number" ? component.quantity : 1,
          isRequired: true,
          ...cleanComponent,
        });
      });
    }

    // Add optional components
    if (Array.isArray(product.components.optional)) {
      product.components.optional.forEach((component, index) => {
        const { isSelected, ...cleanComponent } = component; // Remove isSelected
        normalized.push({
          id: component.id || generateStableId(component.inputId, index + 1000), // Offset to avoid ID conflicts
          name: component.name || "",
          unitPrice:
            typeof component.unitPrice === "number" ? component.unitPrice : 0,
          marginPercent:
            typeof component.marginPercent === "number"
              ? component.marginPercent
              : 0,
          quantity:
            typeof component.quantity === "number" ? component.quantity : 1,
          isRequired: false,
          ...cleanComponent,
        });
      });
    }

    return normalized;
  }

  // Schema C: Very old schema - product.costs?.items (DO NOT USE for editable components)
  // Return empty array - this should not be used for editable components
  console.warn(
    "Found old costs.items schema, not using for editable components"
  );
  return [];
};

// Build computed snapshot for persistence
const buildCalculatedSnapshot = (components, pricingSettings) => {
  const sanitizeNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : Math.max(0, num);
  };

  const calcCogs = (componentsList) => {
    return componentsList.reduce((total, component) => {
      const unitPrice = sanitizeNumber(component.unitPrice);
      const quantity = sanitizeNumber(component.quantity);
      return total + unitPrice * quantity;
    }, 0);
  };

  const calcComponentsBase = (componentsList) => {
    return componentsList.reduce((total, component) => {
      const purchaseTotal = calcComponentPurchaseTotal(
        component.unitPrice,
        component.quantity
      );
      const marginPercent = sanitizeNumber(component.marginPercent, 0);
      return total + purchaseTotal * (1 + marginPercent / 100);
    }, 0);
  };

  const calcRetailNet = (cogs, marginPercent) => {
    const margin = sanitizeNumber(marginPercent, 35);
    if (margin >= 100) {
      throw new Error("Target margin percent must be less than 100%");
    }
    if (cogs === 0) {
      return 0;
    }
    return cogs / (1 - margin / 100);
  };

  const applyRounding = (price, roundingSettings) => {
    if (!roundingSettings.enabled) {
      return price;
    }
    const precision = sanitizeNumber(roundingSettings.precision, 1);
    return Math.round(price / precision) * precision;
  };

  const calcWholesale = (
    retailNet,
    discounts,
    cogs,
    preventBelowCogs = true
  ) => {
    const result = {};
    ["l1", "l2", "l3"].forEach((level) => {
      const discount = Math.min(
        99,
        Math.max(
          0,
          sanitizeNumber(
            discounts[level],
            level === "l1" ? 10 : level === "l2" ? 15 : 20
          )
        )
      );
      let wholesalePrice = retailNet * (1 - discount / 100);
      wholesalePrice = applyRounding(wholesalePrice, pricingSettings.rounding);
      if (preventBelowCogs && wholesalePrice < cogs) {
        wholesalePrice = cogs;
      }
      result[level] = { price: wholesalePrice, discount };
    });
    return result;
  };

  const calcProfit = (price, cogs) => {
    const profitEur = price - cogs;
    const profitPercent = price > 0 ? (profitEur / price) * 100 : 0;
    return {
      profitEur: Math.round(profitEur * 100) / 100,
      profitPercent: Math.round(profitPercent * 10) / 10,
    };
  };

  const requiredComponents = components.filter((comp) => comp.isRequired);

  // Component-level computed values
  const componentCalculations = components.map((comp) => ({
    id: comp.id,
    purchaseTotal: toMoney2(
      calcComponentPurchaseTotal(comp.unitPrice, comp.quantity)
    ),
    totalWithMargin: toMoney2(
      calcComponentTotalWithMargin(
        comp.unitPrice,
        comp.marginPercent || 0,
        comp.quantity
      )
    ),
  }));

  // Variant A (required only)
  const purchaseTotalRequired = toMoney2(calcCogs(requiredComponents));
  const componentsBaseRequired = toMoney2(
    calcComponentsBase(requiredComponents)
  );
  const retailRequired = (() => {
    try {
      let retail = calcRetailNet(
        componentsBaseRequired,
        pricingSettings.targetMarginPercent
      );
      return applyRounding(retail, pricingSettings.rounding);
    } catch (error) {
      return 0;
    }
  })();
  const retailRequiredEur = toMoney2(retailRequired);
  const retailRequiredCzk = toCzkInt(
    retailRequired * pricingSettings.exchangeRates.EUR_CZK
  );
  const wholesaleRequired = calcWholesale(
    retailRequired,
    pricingSettings.wholesaleDiscounts,
    purchaseTotalRequired,
    pricingSettings.validation.preventBelowCogs
  );

  // Variant B (all components)
  const purchaseTotalAll = toMoney2(calcCogs(components));
  const componentsBaseAll = toMoney2(calcComponentsBase(components));
  const retailAll = (() => {
    try {
      let retail = calcRetailNet(
        componentsBaseAll,
        pricingSettings.targetMarginPercent
      );
      return applyRounding(retail, pricingSettings.rounding);
    } catch (error) {
      return 0;
    }
  })();
  const retailAllEur = toMoney2(retailAll);
  const retailAllCzk = toCzkInt(
    retailAll * pricingSettings.exchangeRates.EUR_CZK
  );
  const wholesaleAll = calcWholesale(
    retailAll,
    pricingSettings.wholesaleDiscounts,
    purchaseTotalAll,
    pricingSettings.validation.preventBelowCogs
  );

  // Profit calculations for Variant A (using purchaseTotal)
  const profitRequired = {
    retail: calcProfit(retailRequiredEur, purchaseTotalRequired),
    l1: calcProfit(wholesaleRequired.l1.price, purchaseTotalRequired),
    l2: calcProfit(wholesaleRequired.l2.price, purchaseTotalRequired),
    l3: calcProfit(wholesaleRequired.l3.price, purchaseTotalRequired),
  };

  // Profit calculations for Variant B (using purchaseTotal)
  const profitAll = {
    retail: calcProfit(retailAllEur, purchaseTotalAll),
    l1: calcProfit(wholesaleAll.l1.price, purchaseTotalAll),
    l2: calcProfit(wholesaleAll.l2.price, purchaseTotalAll),
    l3: calcProfit(wholesaleAll.l3.price, purchaseTotalAll),
  };

  return {
    components: componentCalculations,
    variantA: {
      purchaseTotal: purchaseTotalRequired,
      componentsBase: componentsBaseRequired,
      retailEur: retailRequiredEur,
      retailCzk: retailRequiredCzk,
      wholesale: {
        l1: toMoney2(wholesaleRequired.l1.price),
        l2: toMoney2(wholesaleRequired.l2.price),
        l3: toMoney2(wholesaleRequired.l3.price),
      },
      profit: profitRequired,
    },
    variantB: {
      purchaseTotal: purchaseTotalAll,
      componentsBase: componentsBaseAll,
      retailEur: retailAllEur,
      retailCzk: retailAllCzk,
      wholesale: {
        l1: toMoney2(wholesaleAll.l1.price),
        l2: toMoney2(wholesaleAll.l2.price),
        l3: toMoney2(wholesaleAll.l3.price),
      },
      profit: profitAll,
    },
    computedAt: new Date().toISOString(),
  };
};

// Sortable Component Row
const SortableComponentRow = ({
  component,
  index,
  onNameChange,
  onUnitPriceChange,
  onMarginChange,
  onQuantityChange,
  onToggleRequired,
  onRemove,
  formatPrice,
  newRowRef,
  setNewRowRef,
  calculatedSnapshot,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: component.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        "&:last-child td": { borderBottom: 0 },
        "&:hover": {
          backgroundColor: isDragging ? "transparent" : "action.hover",
        },
        ...(component.isRequired && {
          borderLeft: 3,
          borderLeftColor: "success.main",
          "& > td:first-of-type": {
            backgroundColor: "success.light",
            opacity: 0.3,
          },
        }),
      }}
    >
      {/* Drag Handle Column */}
      <TableCell sx={{ py: 0.5, width: 40, cursor: "grab" }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragHandleIcon fontSize="small" color="action" />
        </Box>
      </TableCell>

      {/* Name Column */}
      <TableCell sx={{ py: 0.5 }}>
        <TextField
          size="small"
          value={component.name}
          onChange={(e) => onNameChange(component.id, e.target.value)}
          inputRef={
            index === 0 && !component.name
              ? (ref) => {
                  if (ref && !newRowRef) {
                    setNewRowRef(ref);
                    setTimeout(() => ref.focus(), 100);
                  }
                }
              : null
          }
          placeholder="Názov komponentu"
          variant="outlined"
          fullWidth
        />
      </TableCell>

      {/* Unit Price Input Column */}
      <TableCell sx={{ py: 0.5 }}>
        <TextField
          type="number"
          size="small"
          value={component.unitPrice}
          onChange={(e) => onUnitPriceChange(component.id, e.target.value)}
          inputProps={{
            min: 0,
            step: 0.01,
            style: { textAlign: "center", width: "80px" },
          }}
          variant="outlined"
        />
      </TableCell>

      {/* Quantity Input Column */}
      <TableCell sx={{ py: 0.5 }}>
        <TextField
          type="number"
          size="small"
          value={component.quantity}
          onChange={(e) => onQuantityChange(component.id, e.target.value)}
          inputProps={{
            min: 0,
            step: 1,
            style: { textAlign: "center", width: "50px" },
          }}
          variant="outlined"
        />
      </TableCell>

      {/* Purchase Total Column */}
      <TableCell sx={{ py: 0.5 }}>
        {formatPrice(
          calculatedSnapshot?.components?.find((c) => c.id === component.id)
            ?.purchaseTotal ??
            calcComponentPurchaseTotal(component.unitPrice, component.quantity)
        )}
      </TableCell>

      {/* Total with Margin Column */}
      <TableCell sx={{ py: 0.5 }}>
        {formatPrice(
          calculatedSnapshot?.components?.find((c) => c.id === component.id)
            ?.totalWithMargin ??
            calcComponentTotalWithMargin(
              component.unitPrice,
              component.marginPercent || 0,
              component.quantity
            )
        )}
      </TableCell>

      {/* Margin Input Column */}
      <TableCell sx={{ py: 0.5 }}>
        <TextField
          type="number"
          size="small"
          value={component.marginPercent || 0}
          onChange={(e) => onMarginChange(component.id, e.target.value)}
          inputProps={{
            min: 0,
            max: 99,
            step: 1,
            style: { textAlign: "center", width: "70px" },
          }}
          variant="outlined"
          error={component.marginPercent >= 500}
          helperText={component.marginPercent >= 500 ? "Max 500%" : ""}
        />
      </TableCell>

      {/* Required/Optional Indicator Column */}
      <TableCell sx={{ py: 0.5, width: 100 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: component.isRequired
                ? "success.main"
                : "warning.main",
              color: "white",
              fontWeight: "bold",
              fontSize: "0.7rem",
            }}
          >
            {component.isRequired ? "Povinný" : "Nepovinný"}
          </Typography>
          <Switch
            checked={component.isRequired}
            onChange={(e) => onToggleRequired(component.id, e.target.checked)}
            size="small"
            sx={{ transform: "scale(0.8)" }}
          />
        </Box>
      </TableCell>

      {/* Remove Button Column */}
      <TableCell sx={{ py: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => onRemove(component.id)}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const CalculationsProductDetail = () => {
  const { productKey } = useParams();
  const { t } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [components, setComponents] = useState([]);
  const [, setActiveComponent] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [pricingSettings, setPricingSettings] = useState({
    targetMarginPercent: 35,
    wholesaleDiscounts: { l1: 10, l2: 15, l3: 20 },
    rounding: { enabled: true, method: "nearest", precision: 1 },
    validation: { preventBelowCogs: true },
    exchangeRates: { EUR_CZK: 25.0 },
  });
  const [newRowRef, setNewRowRef] = useState(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [calculatedSnapshot, setCalculatedSnapshot] = useState(null);
  const [parameters, setParameters] = useState({
    weightKg: 0,
    widthMm: 0,
    heightMm: 0,
    depthMm: 0,
  });
  const [isVisibleInPricelist, setIsVisibleInPricelist] = useState(true);
  // Product type editing (defaults to kiosk in UI if missing, but isn't persisted until save)
  const [productType, setProductType] = useState("kiosk"); // "kiosk" | "accessories"
  const [productTypeTouched, setProductTypeTouched] = useState(false);
  const [productTypeMissingInitially, setProductTypeMissingInitially] =
    useState(true);
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [dataSource, setDataSource] = useState(null); // "draft" or "api"
  const [productName, setProductName] = useState("");
  const [productNameError, setProductNameError] = useState("");
  const [pdfDocumentationUrl, setPdfDocumentationUrl] = useState("");
  const [pdfDocumentationUrlError, setPdfDocumentationUrlError] = useState("");
  const editorKeyRef = useRef(0);

  const isMounted = useRef(true);
  const enqueueSnackbarRef = useRef();
  const toastShown = useRef(false);
  const didInitRef = useRef(false); // Prevent double-loading

  const generateId = () => {
    return `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const normalizeUrl = (value) => {
    const v = typeof value === "string" ? value.trim() : "";
    return v;
  };

  const isValidUrl = (value) => {
    const v = normalizeUrl(value);
    if (!v) return true; // empty allowed
    try {
      // eslint-disable-next-line no-new
      new URL(v);
      return true;
    } catch (_e) {
      return false;
    }
  };

  // Store enqueueSnackbar in ref to avoid dependency issues
  enqueueSnackbarRef.current = enqueueSnackbar;

  const product = model?.products?.[productKey];
  const availableInputs = useMemo(() => model?.inputs || {}, [model]);
  const availableInputsRef = useRef(availableInputs);

  // Update ref when availableInputs changes
  availableInputsRef.current = availableInputs;

  // Clear calculated snapshot when inputs change (to trigger recomputation)
  useEffect(() => {
    if (!isDraftLoaded) {
      setCalculatedSnapshot(null);
    }
  }, [components, pricingSettings, isDraftLoaded]);

  // Initialize product name when product data loads
  useEffect(() => {
    if (product?.name) {
      setProductName(product.name);
      setProductNameError("");
    }
  }, [product?.name]);

  // Handle product name changes
  const handleProductNameChange = (event) => {
    const newName = event.target.value;
    setProductName(newName);

    // Validate empty name
    if (!newName.trim()) {
      setProductNameError("Názov produktu nemôže byť prázdny");
    } else {
      setProductNameError("");
    }

    // Update model immediately
    if (model?.products?.[productKey]) {
      const updatedModel = {
        ...model,
        products: {
          ...model.products,
          [productKey]: {
            ...model.products[productKey],
            name: newName.trim(),
          },
        },
      };
      setModel(updatedModel);

      // If working from draft, save to localStorage immediately
      if (isDraftLoaded) {
        const draftKey = getDraftKey(productKey);
        const existingDraft = localStorage.getItem(draftKey);

        if (existingDraft) {
          try {
            const draftData = JSON.parse(existingDraft);
            draftData.product.name = newName.trim();
            draftData.updatedAt = new Date().toISOString();
            localStorage.setItem(draftKey, JSON.stringify(draftData));
          } catch (error) {
            console.error("Error updating draft with new product name:", error);
          }
        }
      }
    }
  };

  const handleProductNameBlur = () => {
    // Trim whitespace on blur
    const trimmedName = productName.trim();
    setProductName(trimmedName);

    // Update model with trimmed name
    if (
      model?.products?.[productKey] &&
      trimmedName !== model.products[productKey].name
    ) {
      const updatedModel = {
        ...model,
        products: {
          ...model.products,
          [productKey]: {
            ...model.products[productKey],
            name: trimmedName,
          },
        },
      };
      setModel(updatedModel);
    }
  };

  useEffect(() => {
    // Prevent double-loading
    if (didInitRef.current) return;

    isMounted.current = true;
    didInitRef.current = true;

    const loadData = async () => {
      try {
        if (isMounted.current) setLoading(true);
        if (isMounted.current) setError(null);

        // Step A: Check localStorage for draft first
        const draftKey = getDraftKey(productKey);
        const draftRaw = localStorage.getItem(draftKey);

        if (draftRaw) {
          try {
            const draft = JSON.parse(draftRaw);

            // Handle both old format (direct properties) and new format (product object)
            if (draft.product) {
              // New format with full product object
              const draftProduct = draft.product;

              // Load components using normalize function for consistency
              const normalizedComponents = normalizeComponents(draftProduct);
              setComponents(normalizedComponents);

              // Load pricing settings
              if (draftProduct.pricingSettings) {
                setPricingSettings({
                  targetMarginPercent:
                    draftProduct.pricingSettings.targetMarginPercent ?? 35,
                  wholesaleDiscounts: {
                    l1:
                      draftProduct.pricingSettings.wholesaleDiscounts?.l1 ?? 10,
                    l2:
                      draftProduct.pricingSettings.wholesaleDiscounts?.l2 ?? 15,
                    l3:
                      draftProduct.pricingSettings.wholesaleDiscounts?.l3 ?? 20,
                  },
                  rounding: {
                    enabled:
                      draftProduct.pricingSettings.rounding?.enabled ?? true,
                    method:
                      draftProduct.pricingSettings.rounding?.method ??
                      "nearest",
                    precision:
                      draftProduct.pricingSettings.rounding?.precision ?? 1,
                  },
                  validation: {
                    preventBelowCogs:
                      draftProduct.pricingSettings.validation
                        ?.preventBelowCogs ?? true,
                  },
                  exchangeRates: {
                    EUR_CZK:
                      draftProduct.pricingSettings.exchangeRates?.EUR_CZK ??
                      25.0,
                  },
                });
              }

              // Load parameters
              if (draftProduct.parameters) {
                setParameters({
                  weightKg: draftProduct.parameters.weightKg ?? 0,
                  widthMm: draftProduct.parameters.widthMm ?? 0,
                  heightMm: draftProduct.parameters.heightMm ?? 0,
                  depthMm: draftProduct.parameters.depthMm ?? 0,
                });
              }

              // Load pricelist visibility
              setIsVisibleInPricelist(
                draftProduct.isVisibleInPricelist !== false
              );
              setPdfDocumentationUrl(
                normalizeUrl(draftProduct.pdfDocumentationUrl)
              );
              setPdfDocumentationUrlError("");
              // Load product type (default UI to kiosk if missing; do not persist until save)
              setProductTypeMissingInitially(
                draftProduct.productType === undefined ||
                  draftProduct.productType === null
              );
              setProductType(
                draftProduct.productType === "accessories" ||
                  draftProduct.productType === "kiosk"
                  ? draftProduct.productType
                  : "kiosk"
              );
              setProductTypeTouched(false);

              // Load description
              let loadedDescription = "";
              if (draftProduct.description?.html) {
                loadedDescription = draftProduct.description.html;
              } else if (
                draftProduct.description &&
                typeof draftProduct.description === "string"
              ) {
                loadedDescription = draftProduct.description;
              } else if (draftProduct.description_html) {
                loadedDescription = draftProduct.description_html;
              } else if (
                draftProduct.productDescription &&
                typeof draftProduct.productDescription === "string"
              ) {
                loadedDescription = draftProduct.productDescription;
              } else if (draftProduct.productDescription?.html) {
                loadedDescription = draftProduct.productDescription.html;
              }

              setDescriptionHtml(loadedDescription || "");

              // Store calculated snapshot for UI rendering
              if (draftProduct.calculated) {
                setCalculatedSnapshot(draftProduct.calculated);
              }

              // Also store the full model for reference (though we don't use it directly)
              setModel({ products: { [productKey]: draftProduct } });
            } else {
              // Old format (backward compatibility)
              const cleanComponents = (draft.components || []).map((comp) => {
                const { isSelected, ...cleanComp } = comp;
                return cleanComp;
              });
              setComponents(cleanComponents);
              if (draft.pricingSettings) {
                setPricingSettings({
                  targetMarginPercent:
                    draft.pricingSettings.targetMarginPercent ?? 35,
                  wholesaleDiscounts: {
                    l1: draft.pricingSettings.wholesaleDiscounts?.l1 ?? 10,
                    l2: draft.pricingSettings.wholesaleDiscounts?.l2 ?? 15,
                    l3: draft.pricingSettings.wholesaleDiscounts?.l3 ?? 20,
                  },
                  rounding: {
                    enabled: draft.pricingSettings.rounding?.enabled ?? true,
                    method: draft.pricingSettings.rounding?.method ?? "nearest",
                    precision: draft.pricingSettings.rounding?.precision ?? 1,
                  },
                  validation: {
                    preventBelowCogs:
                      draft.pricingSettings.validation?.preventBelowCogs ??
                      true,
                  },
                  exchangeRates: {
                    EUR_CZK:
                      draft.pricingSettings.exchangeRates?.EUR_CZK ?? 25.0,
                  },
                });
              }
            }

            // Set flags for draft loading
            setIsDraftLoaded(true);
            setDataSource("draft");

            // Show draft loaded toast
            if (!toastShown.current) {
              enqueueSnackbarRef.current(
                "Načítaný uložený draft tohto produktu.",
                {
                  variant: "info",
                  autoHideDuration: 4000,
                }
              );
              toastShown.current = true;
            }

            return; // Exit early - don't load from API
          } catch (error) {
            console.error("Error loading draft:", error);
            // Remove corrupted draft and fall back to API
            localStorage.removeItem(draftKey);
          }
        }

        // If no draft or draft loading failed, load from API
        const response = await getCalculationsModel();

        if (response.ok === true && response.data) {
          if (isMounted.current) {
            setModel(response.data);

            // Find the product
            const product = response.data.products?.[productKey];
            if (!product) {
              enqueueSnackbarRef.current("Produkt sa nenašiel v dátach.", {
                variant: "error",
                autoHideDuration: 5000,
              });
              setError("Produkt nebol nájdený.");
              return;
            }

            // Normalize components from API data
            const normalizedComponents = normalizeComponents(product);
            setComponents(normalizedComponents);

            // Load pricing settings from API if they exist
            if (product.pricingSettings) {
              setPricingSettings({
                targetMarginPercent:
                  product.pricingSettings.targetMarginPercent ?? 35,
                wholesaleDiscounts: {
                  l1: product.pricingSettings.wholesaleDiscounts?.l1 ?? 10,
                  l2: product.pricingSettings.wholesaleDiscounts?.l2 ?? 15,
                  l3: product.pricingSettings.wholesaleDiscounts?.l3 ?? 20,
                },
                rounding: {
                  enabled: product.pricingSettings.rounding?.enabled ?? true,
                  method: product.pricingSettings.rounding?.method ?? "nearest",
                  precision: product.pricingSettings.rounding?.precision ?? 1,
                },
                validation: {
                  preventBelowCogs:
                    product.pricingSettings.validation?.preventBelowCogs ??
                    true,
                },
                exchangeRates: {
                  EUR_CZK:
                    product.pricingSettings.exchangeRates?.EUR_CZK ?? 25.0,
                },
              });
            }

            // Load parameters from API if they exist
            if (product.parameters) {
              setParameters({
                weightKg: product.parameters.weightKg ?? 0,
                widthMm: product.parameters.widthMm ?? 0,
                heightMm: product.parameters.heightMm ?? 0,
                depthMm: product.parameters.depthMm ?? 0,
              });
            }

            // Load pricelist visibility from API
            setIsVisibleInPricelist(product.isVisibleInPricelist !== false);
            setPdfDocumentationUrl(normalizeUrl(product.pdfDocumentationUrl));
            setPdfDocumentationUrlError("");
            // Load product type from API (default UI to kiosk if missing; do not persist until save)
            setProductTypeMissingInitially(
              product.productType === undefined || product.productType === null
            );
            setProductType(
              product.productType === "accessories" ||
                product.productType === "kiosk"
                ? product.productType
                : "kiosk"
            );
            setProductTypeTouched(false);

            // Load description from API if it exists
            let loadedDescription = "";
            if (product.description?.html) {
              loadedDescription = product.description.html;
            } else if (
              product.description &&
              typeof product.description === "string"
            ) {
              loadedDescription = product.description;
            } else if (product.description_html) {
              loadedDescription = product.description_html;
            } else if (
              product.productDescription &&
              typeof product.productDescription === "string"
            ) {
              loadedDescription = product.productDescription;
            } else if (product.productDescription?.html) {
              loadedDescription = product.productDescription.html;
            }

            setDescriptionHtml(loadedDescription || "");

            // Load calculated snapshot from API if it exists
            if (product.calculated) {
              setCalculatedSnapshot(product.calculated);
            }

            // Set flags for API loading
            setIsDraftLoaded(false);
            setDataSource("api");
          }
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error loading data:", err);
        if (isMounted.current) setError("Nepodarilo sa načítať dáta.");
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted.current = false;
    };
  }, [productKey]);

  const getProductImage = () => {
    if (!product) return "/images/noImagePlaceholder.png";
    return (
      product.image ||
      product.meta?.imageUrl ||
      product.meta?.image ||
      "/images/noImagePlaceholder.png"
    );
  };

  const getProductUrl = () => {
    if (!product) return "";
    return product.url || product.meta?.url || "";
  };

  const formatPrice = (price) => {
    return `${price.toFixed(2)} €`;
  };

  // Pure calculation functions
  const sanitizeNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : Math.max(0, num);
  };

  const getRequiredComponents = () =>
    components.filter((comp) => comp.isRequired);
  const getOptionalComponents = () =>
    components.filter((comp) => !comp.isRequired);

  const calcCogsRequired = () => {
    return getRequiredComponents().reduce(
      (total, comp) =>
        total + calcComponentPurchaseTotal(comp.unitPrice, comp.quantity),
      0
    );
  };

  const calcCogsFull = () => {
    const requiredCogs = calcCogsRequired();
    const optionalCogs = getOptionalComponents().reduce(
      (total, comp) =>
        total + calcComponentPurchaseTotal(comp.unitPrice, comp.quantity),
      0
    );
    return requiredCogs + optionalCogs;
  };

  const calcComponentsBaseRequired = () => {
    return getRequiredComponents().reduce(
      (total, comp) =>
        total +
        calcComponentTotalWithMargin(
          comp.unitPrice,
          comp.marginPercent || 0,
          comp.quantity
        ),
      0
    );
  };

  const calcComponentsBaseFull = () => {
    const requiredBase = calcComponentsBaseRequired();
    const optionalBase = getOptionalComponents().reduce(
      (total, comp) =>
        total +
        calcComponentTotalWithMargin(
          comp.unitPrice,
          comp.marginPercent || 0,
          comp.quantity
        ),
      0
    );
    return requiredBase + optionalBase;
  };

  const calcRetailNet = (cogs, marginPercent) => {
    const margin = sanitizeNumber(marginPercent, 35);

    if (margin >= 100) {
      throw new Error("Target margin percent must be less than 100%");
    }

    if (cogs === 0) {
      return 0;
    }

    return cogs / (1 - margin / 100);
  };

  const applyRounding = (price, roundingSettings) => {
    if (!roundingSettings.enabled) {
      return price;
    }

    const precision = sanitizeNumber(roundingSettings.precision, 1);
    return Math.round(price / precision) * precision;
  };

  const calcWholesale = (
    retailNet,
    discounts,
    cogs,
    preventBelowCogs = true
  ) => {
    const result = {};

    ["l1", "l2", "l3"].forEach((level) => {
      const discount = Math.min(
        99,
        Math.max(
          0,
          sanitizeNumber(
            discounts[level],
            level === "l1" ? 10 : level === "l2" ? 15 : 20
          )
        )
      );
      let wholesalePrice = retailNet * (1 - discount / 100);

      // Apply rounding
      wholesalePrice = applyRounding(wholesalePrice, pricingSettings.rounding);

      // Prevent below COGS if enabled
      if (preventBelowCogs && wholesalePrice < cogs) {
        wholesalePrice = cogs;
        result[`${level}Adjusted`] = true;
      }

      result[level] = {
        price: wholesalePrice,
        discount: discount,
      };
    });

    return result;
  };

  // Computed pricing values - Variant A (Required only)
  const purchaseTotalRequired =
    calculatedSnapshot?.variantA?.purchaseTotal ?? calcCogsRequired();
  const componentsBaseRequired =
    calculatedSnapshot?.variantA?.componentsBase ??
    calcComponentsBaseRequired();
  const retailRequired =
    calculatedSnapshot?.variantA?.retailEur ??
    (() => {
      try {
        let retail = calcRetailNet(
          componentsBaseRequired,
          pricingSettings.targetMarginPercent
        );
        return applyRounding(retail, pricingSettings.rounding);
      } catch (error) {
        return 0; // Invalid margin
      }
    })();

  const wholesaleRequired = calculatedSnapshot?.variantA?.wholesale
    ? {
        l1: {
          price: calculatedSnapshot.variantA.wholesale.l1,
          discount: pricingSettings.wholesaleDiscounts.l1,
        },
        l2: {
          price: calculatedSnapshot.variantA.wholesale.l2,
          discount: pricingSettings.wholesaleDiscounts.l2,
        },
        l3: {
          price: calculatedSnapshot.variantA.wholesale.l3,
          discount: pricingSettings.wholesaleDiscounts.l3,
        },
        l1Adjusted: false,
        l2Adjusted: false,
        l3Adjusted: false,
      }
    : calcWholesale(
        retailRequired,
        pricingSettings.wholesaleDiscounts,
        purchaseTotalRequired,
        pricingSettings.validation.preventBelowCogs
      );

  // Computed pricing values - Variant B (Required + selected optional)
  const purchaseTotalAll =
    calculatedSnapshot?.variantB?.purchaseTotal ?? calcCogsFull();
  const componentsBaseFull =
    calculatedSnapshot?.variantB?.componentsBase ?? calcComponentsBaseFull();
  const retailFull =
    calculatedSnapshot?.variantB?.retailEur ??
    (() => {
      try {
        let retail = calcRetailNet(
          componentsBaseFull,
          pricingSettings.targetMarginPercent
        );
        return applyRounding(retail, pricingSettings.rounding);
      } catch (error) {
        return 0; // Invalid margin
      }
    })();

  const wholesaleFull = calculatedSnapshot?.variantB?.wholesale
    ? {
        l1: {
          price: calculatedSnapshot.variantB.wholesale.l1,
          discount: pricingSettings.wholesaleDiscounts.l1,
        },
        l2: {
          price: calculatedSnapshot.variantB.wholesale.l2,
          discount: pricingSettings.wholesaleDiscounts.l2,
        },
        l3: {
          price: calculatedSnapshot.variantB.wholesale.l3,
          discount: pricingSettings.wholesaleDiscounts.l3,
        },
        l1Adjusted: false,
        l2Adjusted: false,
        l3Adjusted: false,
      }
    : calcWholesale(
        retailFull,
        pricingSettings.wholesaleDiscounts,
        purchaseTotalAll,
        pricingSettings.validation.preventBelowCogs
      );

  // Profit calculation functions
  const calcProfit = (price, cogs) => {
    const profitEur = price - cogs;
    const profitPercent = price > 0 ? (profitEur / price) * 100 : 0;
    return {
      profitEur: Math.round(profitEur * 100) / 100, // Round to 2 decimals
      profitPercent: Math.round(profitPercent * 10) / 10, // Round to 1 decimal
    };
  };

  const deriveVariantProfitTable = (retail, wholesale, cogs, variant) => {
    const storedProfit =
      variant === "A"
        ? calculatedSnapshot?.variantA?.profit
        : calculatedSnapshot?.variantB?.profit;

    const retailProfit = storedProfit?.retail ?? calcProfit(retail, cogs);
    const l1Profit = storedProfit?.l1 ?? calcProfit(wholesale.l1.price, cogs);
    const l2Profit = storedProfit?.l2 ?? calcProfit(wholesale.l2.price, cogs);
    const l3Profit = storedProfit?.l3 ?? calcProfit(wholesale.l3.price, cogs);

    return [
      {
        type: "Maloobchodná cena",
        price: retail,
        profitEur: retailProfit.profitEur,
        profitPercent: retailProfit.profitPercent,
      },
      {
        type: "Veľkoobchodná cena L1",
        price: wholesale.l1.price,
        profitEur: l1Profit.profitEur,
        profitPercent: l1Profit.profitPercent,
        adjusted: wholesale.l1Adjusted,
      },
      {
        type: "Veľkoobchodná cena L2",
        price: wholesale.l2.price,
        profitEur: l2Profit.profitEur,
        profitPercent: l2Profit.profitPercent,
        adjusted: wholesale.l2Adjusted,
      },
      {
        type: "Veľkoobchodná cena L3 (iKelp)",
        price: wholesale.l3.price,
        profitEur: l3Profit.profitEur,
        profitPercent: l3Profit.profitPercent,
        adjusted: wholesale.l3Adjusted,
      },
    ];
  };

  // Profit tables for both variants
  const profitTableRequired = deriveVariantProfitTable(
    retailRequired,
    wholesaleRequired,
    purchaseTotalRequired,
    "A"
  );
  const profitTableFull = deriveVariantProfitTable(
    retailFull,
    wholesaleFull,
    purchaseTotalAll,
    "B"
  );

  const handleAddComponent = () => {
    const newComponent = {
      id: generateId(),
      name: "",
      unitPrice: 0,
      marginPercent: 0,
      quantity: 1,
      isRequired: true, // Default to required
    };

    setComponents((prev) => [newComponent, ...prev]);

    // Auto-focus will be handled by the table row rendering
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveComponent(null);

    if (active.id !== over.id) {
      setComponents((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);

        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveComponent(components.find((item) => item.id === active.id));
  };

  const handleRemoveComponent = (componentId) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure"),
      content: "Naozaj chcete odstrániť tento komponent?",
      onYes: () => {
        setComponents((prev) => prev.filter((item) => item.id !== componentId));
        hideDialog();
      },
      onNo: () => hideDialog(),
    });
  };

  const updateComponent = (componentId, updates) => {
    // Special handling for isRequired changes
    if (updates.isRequired !== undefined) {
      setComponents((prev) =>
        prev.map((item) => {
          if (item.id === componentId) {
            return { ...item, ...updates };
          }
          return item;
        })
      );
    } else {
      // Regular updates
      setComponents((prev) =>
        prev.map((item) =>
          item.id === componentId ? { ...item, ...updates } : item
        )
      );
    }
  };

  const handleQuantityChange = (componentId, newQuantity) => {
    updateComponent(componentId, {
      quantity: Math.max(0, parseInt(newQuantity) || 0),
    });
  };

  const handleNameChange = (componentId, newName) => {
    updateComponent(componentId, { name: newName });
  };

  const handleUnitPriceChange = (componentId, newUnitPrice) => {
    updateComponent(componentId, { unitPrice: sanitizeNumber(newUnitPrice) });
  };

  const handleMarginChange = (componentId, newMarginPercent) => {
    updateComponent(componentId, {
      marginPercent: sanitizeNumber(newMarginPercent),
    });
  };

  const handlePricingSettingChange = (path, value) => {
    setPricingSettings((prev) => {
      const newSettings = { ...prev };
      const keys = path.split(".");
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]] = { ...current[keys[i]] };
      }

      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const getDraftKey = (productKey) => {
    return `dsok.calculations.draft.${productKey}`;
  };

  const handleSaveDraft = () => {
    const calculatedSnapshot = buildCalculatedSnapshot(
      components,
      pricingSettings
    );

    const allowedProductTypes = ["kiosk", "accessories"];
    const shouldPersistProductType =
      productTypeTouched || !productTypeMissingInitially;
    const nextProductType = productType || "kiosk";

    if (
      shouldPersistProductType &&
      !allowedProductTypes.includes(String(nextProductType))
    ) {
      enqueueSnackbar("Neplatný typ produktu. Uloženie draftu bolo zrušené.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      return;
    }

    const draftData = {
      productKey,
      updatedAt: new Date().toISOString(),
      product: {
        ...product, // Include all server product data
        components,
        pricingSettings,
        calculated: calculatedSnapshot,
        parameters,
        isVisibleInPricelist,
        pdfDocumentationUrl: normalizeUrl(pdfDocumentationUrl) || undefined,
        ...(shouldPersistProductType ? { productType: nextProductType } : {}),
        description: {
          html: descriptionHtml,
        },
      },
    };

    try {
      localStorage.setItem(getDraftKey(productKey), JSON.stringify(draftData));

      // Update UI state immediately to show draft indicator
      setDataSource("draft");
      setIsDraftLoaded(true);

      enqueueSnackbar("Draft produktu bol uložený.", {
        variant: "info",
        autoHideDuration: 4000,
      });
    } catch (error) {
      console.error("Error saving draft:", error);
      enqueueSnackbar("Draft sa nepodarilo uložiť.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple concurrent saves

    setIsSaving(true);

    try {
      if (!isValidUrl(pdfDocumentationUrl)) {
        setPdfDocumentationUrlError("Neplatná URL adresa");
        return;
      }
      setPdfDocumentationUrlError("");

      // 1. Fetch the latest model from backend
      const latestResponse = await getCalculationsModel();

      if (!latestResponse.ok || !latestResponse.data) {
        throw new Error("Failed to fetch latest model");
      }

      const latestModel = latestResponse.data;

      // 2. Get the current product from server
      const currentProductFromServer = latestModel.products?.[productKey];

      if (!currentProductFromServer) {
        throw new Error("Product not found in latest model");
      }

      // 3. Build calculated snapshot
      const calculatedSnapshot = buildCalculatedSnapshot(
        components,
        pricingSettings
      );

      const allowedProductTypes = ["kiosk", "accessories"];
      const shouldPersistProductType =
        productTypeTouched ||
        (currentProductFromServer.productType !== undefined &&
          currentProductFromServer.productType !== null);
      const nextProductType = productType || "kiosk";

      if (
        shouldPersistProductType &&
        !allowedProductTypes.includes(String(nextProductType))
      ) {
        enqueueSnackbar("Neplatný typ produktu. Uloženie bolo zrušené.", {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }

      // 4. Create updated product immutably by merging ONLY the edited fields.
      // Preserve all nested data from the server product (components/BOM/meta/etc.) by starting from the full server object.
      const updatedProduct = {
        ...currentProductFromServer,
        name: productName.trim(), // Include updated product name
        components,
        pricingSettings,
        calculated: calculatedSnapshot,
        parameters,
        isVisibleInPricelist,
        pdfDocumentationUrl: normalizeUrl(pdfDocumentationUrl) || undefined,
        ...(shouldPersistProductType ? { productType: nextProductType } : {}),
        description: {
          html: descriptionHtml,
        },
      };

      // 5. Update the model immutably (do NOT mutate latestModel in-place)
      const nextModel =
        typeof structuredClone === "function"
          ? structuredClone(latestModel)
          : JSON.parse(JSON.stringify(latestModel));
      nextModel.products = nextModel.products || {};
      nextModel.products[productKey] = updatedProduct;
      nextModel.generatedAt = new Date().toISOString();

      // 6. Save the full model back to backend
      const saveResponse = await saveCalculationsModel(nextModel);

      if (saveResponse?.ok === false) {
        throw new Error("Save operation failed");
      }

      // 7. Success: Update stores and form state, clear draft
      enqueueSnackbar("Produkt bol úspešne uložený.", {
        variant: "success",
      });

      // Update global cache with saved data
      calculationsCache.set(nextModel);

      // Update local model state to match saved data
      setModel(nextModel);

      // Update form states to match the saved product data
      const savedProduct = updatedProduct; // This is the product we just saved
      setProductName(savedProduct.name || "");
      setComponents(savedProduct.components || []);
      setPricingSettings(
        savedProduct.pricingSettings || {
          targetMarginPercent: 35,
          wholesaleDiscounts: { l1: 10, l2: 15, l3: 20 },
          rounding: { enabled: true, method: "nearest", precision: 1 },
          validation: { preventBelowCogs: true },
          exchangeRates: { EUR_CZK: 25.0 },
        }
      );
      setParameters(
        savedProduct.parameters || {
          weightKg: 0,
          widthMm: 0,
          heightMm: 0,
          depthMm: 0,
        }
      );
      setIsVisibleInPricelist(savedProduct.isVisibleInPricelist !== false);
      setProductTypeMissingInitially(
        savedProduct.productType === undefined ||
          savedProduct.productType === null
      );
      setProductType(
        savedProduct.productType === "accessories" ||
          savedProduct.productType === "kiosk"
          ? savedProduct.productType
          : "kiosk"
      );
      setProductTypeTouched(false);
      setDescriptionHtml(savedProduct.description?.html || "");
      setPdfDocumentationUrl(normalizeUrl(savedProduct.pdfDocumentationUrl));
      setCalculatedSnapshot(savedProduct.calculated);

      // Clear draft for this product (do this AFTER updating stores and form states)
      localStorage.removeItem(getDraftKey(productKey));
      setIsDraftLoaded(false); // Hide DRAFT badge

      // Update data source to indicate we're now using saved API data
      setDataSource("api");
    } catch (error) {
      console.error("Error saving product:", error);

      // Show error notification
      enqueueSnackbar("Uloženie sa nepodarilo. Skúste to prosím znova.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Načítavam produkt...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Detail produktu
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Detail produktu
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Produkt nebol nájdený.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          to="/calculations"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Kalkulácie
        </Link>
        <Typography color="text.primary">{product?.name || "…"}</Typography>
      </Breadcrumbs>

      {/* Product Header */}
      <Card
        sx={{
          mb: 3,
          position: "sticky",
          top: 60, // Account for global sticky menu height (60px min height)
          zIndex: 10,
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <Avatar
              variant="rounded"
              src={getProductImage()}
              alt={product.name}
              sx={{
                width: "auto",
                height: "auto",
                maxHeight: 130,
                maxWidth: 130,
                "& img": {
                  objectFit: "contain",
                  width: "auto",
                  height: "auto",
                  maxHeight: 130,
                  maxWidth: 130,
                },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  variant="standard"
                  value={productName}
                  onChange={handleProductNameChange}
                  onBlur={handleProductNameBlur}
                  error={!!productNameError}
                  helperText={productNameError}
                  sx={{
                    "& .MuiInput-root": {
                      fontSize: "2.125rem", // h4 equivalent
                      fontWeight: 400,
                      lineHeight: 1.235,
                      letterSpacing: "0em",
                    },
                    "& .MuiInput-underline:before": {
                      borderBottom: "none",
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottom: "2px solid rgba(0, 0, 0, 0.42)",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: "2px solid",
                      borderBottomColor: "primary.main",
                    },
                    minWidth: "300px",
                  }}
                  placeholder="Názov produktu"
                />
                {isDraftLoaded && (
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor: "info.main",
                      color: "info.contrastText",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      cursor: "help",
                      title: "Produkt ešte nebol uložený. Pracujete s draftom.",
                    }}
                  >
                    DRAFT
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor:
                      dataSource === "api" ? "success.light" : "warning.light",
                    color:
                      dataSource === "api" ? "success.dark" : "warning.dark",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    ml: 1,
                  }}
                >
                  Zdroj: {dataSource === "api" ? "API" : "Draft"}
                </Typography>
              </Box>

              {/* Pricelist visibility toggle */}
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="subtitle2" sx={{ fontSize: "0.875rem" }}>
                  Zobraziť v cenníku
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isVisibleInPricelist}
                      onChange={(e) =>
                        setIsVisibleInPricelist(e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                      {isVisibleInPricelist ? "Áno" : "Nie"}
                    </Typography>
                  }
                />
              </Box>

              {/* PDF dokumentácia */}
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  size="small"
                  label="PDF dokumentácia (URL)"
                  value={pdfDocumentationUrl}
                  onChange={(e) => {
                    setPdfDocumentationUrl(e.target.value);
                    if (pdfDocumentationUrlError) setPdfDocumentationUrlError("");
                  }}
                  error={!!pdfDocumentationUrlError}
                  helperText={pdfDocumentationUrlError || " "}
                  fullWidth
                />
                {normalizeUrl(pdfDocumentationUrl) ? (
                  <Button
                    variant="outlined"
                    startIcon={<LaunchIcon />}
                    href={normalizeUrl(pdfDocumentationUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    PDF
                  </Button>
                ) : null}
              </Box>

              {/* Typ produktu */}
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="product-type-label">Typ produktu</InputLabel>
                  <Select
                    labelId="product-type-label"
                    value={productType || "kiosk"}
                    label="Typ produktu"
                    onChange={(e) => {
                      setProductType(String(e.target.value || "kiosk"));
                      setProductTypeTouched(true);
                    }}
                  >
                    <MenuItem value="kiosk">Kiosk</MenuItem>
                    <MenuItem value="accessories">Doplnky</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Parametre produktu */}
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  Parametre produktu
                </Typography>
                <Table
                  size="small"
                  sx={{
                    minWidth: "auto",
                    "& .MuiTableCell-root": { px: 0.5, py: 0.25 },
                  }}
                >
                  <TableBody>
                    {/* Labels row */}
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          width: "80px",
                          textAlign: "center",
                          border: "none",
                        }}
                      >
                        Váha (kg)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          width: "80px",
                          textAlign: "center",
                          border: "none",
                        }}
                      >
                        Šírka (mm)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          width: "80px",
                          textAlign: "center",
                          border: "none",
                        }}
                      >
                        Výška (mm)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          width: "80px",
                          textAlign: "center",
                          border: "none",
                        }}
                      >
                        Hĺbka (mm)
                      </TableCell>
                    </TableRow>
                    {/* Values row */}
                    <TableRow>
                      <TableCell sx={{ textAlign: "center", border: "none" }}>
                        <TextField
                          size="small"
                          type="number"
                          value={parameters.weightKg}
                          onChange={(e) => {
                            const value = Math.max(
                              0,
                              parseFloat(e.target.value) || 0
                            );
                            setParameters((prev) => ({
                              ...prev,
                              weightKg: value,
                            }));
                          }}
                          inputProps={{
                            min: 0,
                            step: 0.01,
                            style: {
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                              textAlign: "center",
                            },
                          }}
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "28px",
                              width: "70px",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", border: "none" }}>
                        <TextField
                          size="small"
                          type="number"
                          value={parameters.widthMm}
                          onChange={(e) => {
                            const value = Math.max(
                              0,
                              Math.round(parseFloat(e.target.value) || 0)
                            );
                            setParameters((prev) => ({
                              ...prev,
                              widthMm: value,
                            }));
                          }}
                          inputProps={{
                            min: 0,
                            step: 1,
                            style: {
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                              textAlign: "center",
                            },
                          }}
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "28px",
                              width: "70px",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", border: "none" }}>
                        <TextField
                          size="small"
                          type="number"
                          value={parameters.heightMm}
                          onChange={(e) => {
                            const value = Math.max(
                              0,
                              Math.round(parseFloat(e.target.value) || 0)
                            );
                            setParameters((prev) => ({
                              ...prev,
                              heightMm: value,
                            }));
                          }}
                          inputProps={{
                            min: 0,
                            step: 1,
                            style: {
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                              textAlign: "center",
                            },
                          }}
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "28px",
                              width: "70px",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", border: "none" }}>
                        <TextField
                          size="small"
                          type="number"
                          value={parameters.depthMm}
                          onChange={(e) => {
                            const value = Math.max(
                              0,
                              Math.round(parseFloat(e.target.value) || 0)
                            );
                            setParameters((prev) => ({
                              ...prev,
                              depthMm: value,
                            }));
                          }}
                          inputProps={{
                            min: 0,
                            step: 1,
                            style: {
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                              textAlign: "center",
                            },
                          }}
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "28px",
                              width: "70px",
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  editorKeyRef.current += 1;
                  setDescriptionModalOpen(true);
                }}
                disabled={loading}
                sx={{ mr: 1 }}
              >
                Popis produktu
                {descriptionHtml && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ ml: 0.5, fontSize: "0.7rem" }}
                  >
                    Vyplnené
                  </Typography>
                )}
              </Button>
              <IconButton
                size="small"
                onClick={() => setLegendOpen(true)}
                title="Vysvetlenie pojmov"
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
              {getProductUrl() && (
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  href={getProductUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Web produktu
                </Button>
              )}
              <Button variant="outlined" onClick={handleSaveDraft}>
                Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || !!productNameError}
                startIcon={isSaving ? <CircularProgress size={16} /> : null}
              >
                {isSaving ? "Ukladanie..." : "Uložiť"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Components Section */}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, pb: 1 }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddComponent}
              size="small"
            >
              Pridať komponent
            </Button>
          </Stack>

          {components.length > 0 && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ maxHeight: 400 }}
            >
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    px: 0.625, // 5px horizontal padding (0.625 * 8px = 5px)
                    py: 0.25,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: "bold", py: 1, width: 40 }}
                    ></TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1 }}>
                      Komponent
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1, width: 120 }}>
                      Cena / ks
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1, width: 80 }}>
                      Počet ks
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1, width: 120 }}>
                      Nákupná cena
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1 }}>
                      Spolu
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1, width: 100 }}>
                      Marža (%)
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", py: 1, width: 100 }}>
                      Povinný
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", py: 1, width: 50 }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={components.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {components.map((component, index) => (
                        <SortableComponentRow
                          key={component.id}
                          component={component}
                          index={index}
                          onNameChange={handleNameChange}
                          onUnitPriceChange={handleUnitPriceChange}
                          onMarginChange={handleMarginChange}
                          onQuantityChange={handleQuantityChange}
                          onToggleRequired={(componentId, isRequired) =>
                            updateComponent(componentId, { isRequired })
                          }
                          onRemove={handleRemoveComponent}
                          formatPrice={formatPrice}
                          newRowRef={newRowRef}
                          setNewRowRef={setNewRowRef}
                          calculatedSnapshot={calculatedSnapshot}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Nastavenia cien */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        Nastavenia cien
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <TextField
              label="Cieľová marža (%)"
              type="number"
              size="small"
              value={pricingSettings.targetMarginPercent}
              onChange={(e) =>
                handlePricingSettingChange(
                  "targetMarginPercent",
                  parseFloat(e.target.value) || 0
                )
              }
              inputProps={{ min: 0, max: 99 }}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label="Zľava L1 (%)"
              type="number"
              size="small"
              value={pricingSettings.wholesaleDiscounts.l1}
              onChange={(e) =>
                handlePricingSettingChange(
                  "wholesaleDiscounts.l1",
                  parseFloat(e.target.value) || 0
                )
              }
              inputProps={{ min: 0, max: 99 }}
              sx={{ minWidth: 180 }}
            />

            <TextField
              label="Zľava L2 (%)"
              type="number"
              size="small"
              value={pricingSettings.wholesaleDiscounts.l2}
              onChange={(e) =>
                handlePricingSettingChange(
                  "wholesaleDiscounts.l2",
                  parseFloat(e.target.value) || 0
                )
              }
              inputProps={{ min: 0, max: 99 }}
              sx={{ minWidth: 180 }}
            />

            <TextField
              label="Zľava L3 (%)"
              type="number"
              size="small"
              value={pricingSettings.wholesaleDiscounts.l3}
              onChange={(e) =>
                handlePricingSettingChange(
                  "wholesaleDiscounts.l3",
                  parseFloat(e.target.value) || 0
                )
              }
              inputProps={{ min: 0, max: 99 }}
              sx={{ minWidth: 180 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Presnosť zaokrúhľovania</InputLabel>
              <Select
                value={pricingSettings.rounding.precision}
                onChange={(e) =>
                  handlePricingSettingChange(
                    "rounding.precision",
                    parseFloat(e.target.value)
                  )
                }
                label="Presnosť zaokrúhľovania"
              >
                <MenuItem value={0.1}>0.1 €</MenuItem>
                <MenuItem value={0.5}>0.5 €</MenuItem>
                <MenuItem value={1}>1 €</MenuItem>
                <MenuItem value={10}>10 €</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={pricingSettings.rounding.enabled}
                  onChange={(e) =>
                    handlePricingSettingChange(
                      "rounding.enabled",
                      e.target.checked
                    )
                  }
                />
              }
              label="Zaokrúhľovanie zapnuté"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={pricingSettings.validation.preventBelowCogs}
                  onChange={(e) =>
                    handlePricingSettingChange(
                      "validation.preventBelowCogs",
                      e.target.checked
                    )
                  }
                />
              }
              label="Zabrániť cene nižšej ako náklady"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Pricing Results */}
      <Typography variant="h5" component="h2" gutterBottom>
        Ceny
      </Typography>

      {/* Profit Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h4" gutterBottom>
            Ziskové rozpisy
          </Typography>

          {/* Variant A Profit */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Variant A – iba povinné komponenty
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", py: 1 }}>
                    Typ ceny
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      py: 1,
                      textAlign: "right",
                      backgroundColor: "grey.50",
                    }}
                  >
                    Nákupná cena
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      py: 1,
                      textAlign: "right",
                      backgroundColor: "primary.50",
                    }}
                  >
                    Cena
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", py: 1, textAlign: "right" }}
                  >
                    Zisk (EUR)
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", py: 1, textAlign: "right" }}
                  >
                    Zisk (%)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profitTableRequired.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ py: 0.5 }}>{row.type}</TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {formatPrice(
                          calculatedSnapshot?.variantA?.purchaseTotal ??
                            purchaseTotalRequired
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        backgroundColor: "primary.50",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {formatPrice(row.price)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        color:
                          row.profitEur < 0 ? "error.main" : "text.primary",
                        fontWeight: row.profitEur < 0 ? "bold" : "normal",
                      }}
                    >
                      {row.profitEur >= 0
                        ? formatPrice(row.profitEur)
                        : `-${formatPrice(Math.abs(row.profitEur))}`}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        color:
                          row.profitPercent < 0 ? "error.main" : "text.primary",
                        fontWeight: row.profitPercent < 0 ? "bold" : "normal",
                      }}
                    >
                      {row.profitPercent >= 0
                        ? `${row.profitPercent}%`
                        : `-${Math.abs(row.profitPercent)}%`}
                      {row.adjusted && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ ml: 1 }}
                        >
                          upravené
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Variant B Profit */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Variant B – všetky komponenty
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", py: 1 }}>
                    Typ ceny
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      py: 1,
                      textAlign: "right",
                      backgroundColor: "grey.50",
                    }}
                  >
                    Nákupná cena
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      py: 1,
                      textAlign: "right",
                      backgroundColor: "primary.50",
                    }}
                  >
                    Cena
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", py: 1, textAlign: "right" }}
                  >
                    Zisk (EUR)
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", py: 1, textAlign: "right" }}
                  >
                    Zisk (%)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profitTableFull.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ py: 0.5 }}>{row.type}</TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {formatPrice(
                          calculatedSnapshot?.variantB?.purchaseTotal ??
                            purchaseTotalAll
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        backgroundColor: "primary.50",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {formatPrice(row.price)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        color:
                          row.profitEur < 0 ? "error.main" : "text.primary",
                        fontWeight: row.profitEur < 0 ? "bold" : "normal",
                      }}
                    >
                      {row.profitEur >= 0
                        ? formatPrice(row.profitEur)
                        : `-${formatPrice(Math.abs(row.profitEur))}`}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        textAlign: "right",
                        color:
                          row.profitPercent < 0 ? "error.main" : "text.primary",
                        fontWeight: row.profitPercent < 0 ? "bold" : "normal",
                      }}
                    >
                      {row.profitPercent >= 0
                        ? `${row.profitPercent}%`
                        : `-${Math.abs(row.profitPercent)}%`}
                      {row.adjusted && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ ml: 1 }}
                        >
                          upravené
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {/* Legend Dialog */}
      <Dialog
        open={legendOpen}
        onClose={() => setLegendOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vysvetlenie pojmov</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                COGS (Cost of Goods Sold)
              </Typography>
              <Typography variant="body2">
                COGS sú náklady na produkt – súčet cien všetkých komponentov:
                (cena za kus × počet kusov).
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Maloobchodná cena
              </Typography>
              <Typography variant="body2">
                Maloobchodná cena je predajná cena pre koncového zákazníka.
                Vypočíta sa z COGS a nastavenej cieľovej marže.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Cieľová marža (%)
              </Typography>
              <Typography variant="body2">
                Cieľová marža určuje, akú časť z predajnej ceny tvorí zisk.
                Vyššia marža = vyššia predajná cena.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Kurz EUR/CZK
              </Typography>
              <Typography variant="body2">
                Výmenný kurz používaný na prevod cien z EUR do CZK. Zobrazuje sa
                pri maloobchodných cenách pre informačné účely.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Veľkoobchodné ceny (L1 / L2 / L3)
              </Typography>
              <Typography variant="body2">
                Veľkoobchodné ceny sú cenové úrovne pre partnerov. Počítajú sa
                ako zľava z maloobchodnej ceny.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Nepovinné komponenty
              </Typography>
              <Typography variant="body2">
                Nepovinné komponenty sú doplnky. Variant A zahŕňa iba povinné
                komponenty. Variant B zahŕňa všetky komponenty (povinné +
                nepovinné).
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Zaokrúhľovanie zapnuté
              </Typography>
              <Typography variant="body2">
                Ak je zapnuté zaokrúhľovanie, výsledné ceny sa upravia na
                'krajšie' čísla podľa nastavenej presnosti (napr. na celé eurá).
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Presnosť zaokrúhľovania
              </Typography>
              <Typography variant="body2">
                Určuje, na aké kroky sa zaokrúhľuje (napr. 1 € = celé eurá, 0,5
                € = na 50 centov).
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Zabrániť cene nižšej ako náklady
              </Typography>
              <Typography variant="body2">
                Zabezpečí, že veľkoobchodná cena neklesne pod COGS (náklady),
                aby produkt nešiel do straty.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                DRAFT
              </Typography>
              <Typography variant="body2">
                Draft je dočasne uložená rozpracovaná verzia v prehliadači
                (Local Storage). Zatiaľ nebola uložená do systému.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegendOpen(false)}>Zavrieť</Button>
        </DialogActions>
      </Dialog>

      {/* Product Description Modal */}
      <Dialog
        open={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Popis produktu</DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
          {/* Force re-mount when modal opens with key */}
          {descriptionModalOpen && (
            <CKEditor
              key={`editor-${productKey}-${editorKeyRef.current}`}
              initData={descriptionHtml}
              onChange={(event) => {
                setDescriptionHtml(event.editor.getData());
              }}
              config={{
                toolbar: [
                  ["Bold", "Italic", "Underline"],
                  ["NumberedList", "BulletedList"],
                  ["Link", "Unlink"],
                  ["Undo", "Redo"],
                ],
                height: 300,
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionModalOpen(false)}>
            Zavrieť
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalculationsProductDetail;
